import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookmarkSimple, CaretLeft, Play, Trash, Clock } from "@phosphor-icons/react";
import { VideoCard } from "@/components/VideoCard";
import { SkeletonCard } from "@/components/SkeletonCard";
import { fetchVideo, formatDuration } from "@/lib/api";
import { useWatchLater } from "@/hooks/useWatchLater";
import { useAuth } from "@/context/AuthContext";
import { AccountButton } from "@/components/AccountButton";

export default function Vault() {
  const { ids, clear } = useWatchLater();
  const { user, loading: authLoading, openSignIn } = useAuth();
  const guest = !authLoading && !user;
  const wasSignedIn = useRef(false);

  useEffect(() => {
    if (user) wasSignedIn.current = true;
    // Auto-prompt guests who open the Vault — but not right after they sign out here.
    if (guest && !wasSignedIn.current) openSignIn("vault");
  }, [guest, user]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const key = ids.join(",");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      if (ids.length === 0) {
        setVideos([]);
        setLoading(false);
        return;
      }
      const results = await Promise.allSettled(ids.map((id) => fetchVideo(id)));
      if (!active) return;
      setVideos(results.filter((r) => r.status === "fulfilled").map((r) => r.value));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [key]); // eslint-disable-line

  const totalSec = videos.reduce((acc, v) => acc + (v.duration_sec || 0), 0);
  const playAll = () => {
    if (videos.length) navigate(`/watch/${videos[0].video_id}?autoplay=1`);
  };
  const confirmClear = () => {
    if (window.confirm("Remove all films from your vault?")) clear();
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>My Vault · ReelVault</title>
      </Helmet>

      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10">
        <div className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-4 flex items-center gap-4">
          <Link
            to="/"
            data-testid="vault-back"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/70 hover:text-amber transition-colors duration-300"
          >
            <CaretLeft size={16} /> Films
          </Link>
          <Link to="/" className="ml-auto flex items-center gap-2 group">
            <img src="/reelvault-mark.png" alt="ReelVault" data-testid="brand-logo" className="h-7 w-auto group-hover:scale-105 transition-transform duration-300" />
            <span className="font-serif text-xl tracking-tight text-[#EDEDED] hidden sm:block">
              Reel<span className="text-crimson">Vault</span>
            </span>
          </Link>
          <AccountButton />
        </div>
      </header>

      {guest ? (
        <main data-testid="vault-guest-gate" className="mx-auto max-w-[640px] px-5 sm:px-8 py-24 text-center rv-fade">
          <BookmarkSimple size={44} weight="duotone" className="mx-auto text-amber" />
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tighter text-[#EDEDED]">Your Vault, on every device</h1>
          <p className="mt-4 text-sm sm:text-base text-white/55 leading-relaxed">
            Sign in with Google or a one-tap email link to save titles and pick up where you left off — free, no password.
            {ids.length > 0 && ` The ${ids.length} title${ids.length === 1 ? "" : "s"} saved on this device will move into your account automatically.`}
          </p>
          <button
            data-testid="vault-signin-btn"
            onClick={() => openSignIn("vault")}
            className="rv-cta-glow mt-8 inline-flex items-center gap-2 bg-amber px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-black"
          >
            Sign in to open your Vault
          </button>
          <p className="mt-6"><Link to="/" className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35 hover:text-amber transition-colors">Keep browsing as guest</Link></p>
        </main>
      ) : (
      <main className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-12 rv-fade">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <BookmarkSimple size={28} weight="duotone" className="text-amber" />
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-[#EDEDED]">My Vault</h1>
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 flex items-center gap-3">
              <span>{ids.length} saved {ids.length === 1 ? "film" : "films"}</span>
              {totalSec > 0 && (
                <span className="flex items-center gap-1.5 text-white/30">
                  <Clock size={13} weight="duotone" className="text-amber/70" />
                  {formatDuration(totalSec)} total
                </span>
              )}
            </p>
          </div>
          {videos.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                data-testid="vault-play-all"
                onClick={playAll}
                className="flex items-center gap-2 rounded-full bg-amber px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-black font-semibold hover:bg-crimson hover:text-white transition-colors duration-300"
              >
                <Play size={15} weight="fill" /> Play All
              </button>
              <button
                data-testid="vault-clear"
                onClick={confirmClear}
                className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white/60 hover:border-crimson hover:text-crimson transition-colors duration-300"
              >
                <Trash size={15} weight="duotone" /> Clear
              </button>
            </div>
          )}
        </div>

        {loading && ids.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: Math.min(ids.length, 8) }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="vault-empty">
            <BookmarkSimple size={64} weight="light" className="text-white/20 mb-6" />
            <p className="font-serif text-2xl text-white/70">Your vault is empty</p>
            <p className="mt-2 text-white/40 text-sm">Tap the bookmark on any film to save it for later.</p>
            <Link
              to="/"
              className="mt-6 border border-white/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-amber hover:border-amber transition-colors duration-300"
            >
              Browse films
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8"
            data-testid="vault-grid"
          >
            {videos.map((v, i) => (
              <VideoCard key={v.video_id} video={v} index={i} />
            ))}
          </div>
        )}
      </main>
      )}
    </div>
  );
}
